import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { PROFILE } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

const LINK_DEFS = [
  { key: "home", to: "/#home" },
  { key: "about", to: "/#about" },
  { key: "myWork", to: "/#experience" },
  { key: "currentProduct", to: "/#product" },
  { key: "vision", to: "/#vision" },
  { key: "knowledgeHub", to: "/knowledge" },
  { key: "contact", to: "/#contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const overDark = !scrolled && loc.pathname === "/";
  const { lang, toggleLang, t } = useLanguage();

  const LINKS = LINK_DEFS.map((l) => ({ ...l, label: t.nav[l.key] }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (loc.pathname !== "/") {
        window.location.href = to;
      } else {
        const el = document.getElementById(id);
        if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
        else el?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-paper/70 border-b border-line" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" onClick={() => go("/#home")} className={`font-display font-extrabold text-lg tracking-tight transition-colors duration-300 ${overDark ? "text-paper" : "text-ink"}`} data-testid="nav-logo">
          FR<span className={overDark ? "text-aurora" : "text-auroraDeep"}>.</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) =>
            l.to.startsWith("/#") ? (
              <button key={l.key} onClick={() => go(l.to)} className={`font-mono-tech text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${overDark ? "text-paper/70 hover:text-paper" : "text-stone hover:text-ink"}`} data-testid={`nav-${l.key}`}>
                {l.label}
              </button>
            ) : (
              <Link key={l.key} to={l.to} className={`font-mono-tech text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${overDark ? "text-paper/70 hover:text-paper" : "text-stone hover:text-ink"}`} data-testid={`nav-${l.key}`}>
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 font-mono-tech text-xs uppercase tracking-[0.12em] transition-colors duration-300 ${overDark ? "border-paper/50 text-paper hover:bg-paper hover:text-midnight" : "border-ink text-ink hover:bg-ink hover:text-paper"}`}
            data-testid="nav-lang-toggle"
            aria-label="Toggle language"
          >
            <Languages size={14} />
            <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
            <span aria-hidden="true">/</span>
            <span className={lang === "es" ? "opacity-100" : "opacity-40"}>ES</span>
          </button>
          <a href={PROFILE.cvUrl} target="_blank" rel="noreferrer" className={`hidden sm:inline-flex items-center gap-1.5 rounded-full border px-5 py-2 font-mono-tech text-xs uppercase tracking-[0.12em] transition-colors duration-300 ${overDark ? "border-paper/50 text-paper hover:bg-paper hover:text-midnight" : "border-ink text-ink hover:bg-ink hover:text-paper"}`} data-testid="nav-download-cv">
            {t.nav.downloadCv} <ArrowUpRight size={14} />
          </a>
          <button className={`lg:hidden transition-colors duration-300 ${overDark ? "text-paper" : "text-ink"}`} onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-paper border-b border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l.key} onClick={() => go(l.to)} className="text-left font-display text-2xl" data-testid={`mobile-nav-${l.key}`}>
                  {l.label}
                </button>
              ))}
              <a href={PROFILE.cvUrl} target="_blank" rel="noreferrer" className="mt-2 font-mono-tech text-xs uppercase tracking-widest text-auroraDeep">{t.nav.downloadCv} →</a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
